import React, { useState, useEffect, useMemo } from "react";
import articlesData from "@/data/mdx/generated/articles.json";
import * as StyledArticleArchive from "./Styled.ArticleArchive";

// Configuration constants
const ARTICLES_PER_PAGE = 12;

// Individual article card component - memoized for performance
const ArticleCard = ({ article }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <article className="article-card">
      <StyledArticleArchive.ArticleLink
        to={`/articles/${article.slug}`}
        data-animate="fade-up"
      >
        <div className="article-content">
          {article.frontmatter.featured && (
            <div className="featured-badge">Featured</div>
          )}

          <StyledArticleArchive.ArticleTitle as="h2">
            {article.frontmatter.title}
          </StyledArticleArchive.ArticleTitle>

          <p>{article.excerpt}</p>

          <StyledArticleArchive.ChipLabelWrapper>
            {article.frontmatter.tags &&
              article.frontmatter.tags.length > 0 &&
              article.frontmatter.tags.map((tag) => (
                <StyledArticleArchive.ChipLabel key={tag}>
                  {tag}
                </StyledArticleArchive.ChipLabel>
              ))}
          </StyledArticleArchive.ChipLabelWrapper>
        </div>
      </StyledArticleArchive.ArticleLink>
    </article>
  );
};

// Memoize ArticleCard to prevent unnecessary re-renders
const MemoizedArticleCard = React.memo(ArticleCard);

// Main ArticleArchive component
export const ArticleArchive = () => {
  const [articles] = useState(articlesData.articles || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Memoized filtered articles - only recalculates when filters change
  const filteredArticles = useMemo(() => {
    let filtered = articles;

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(
        (article) => article.frontmatter.category === selectedCategory
      );
    }

    // Apply tag filter
    if (selectedTag) {
      filtered = filtered.filter((article) =>
        article.frontmatter.tags?.includes(selectedTag)
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (article) =>
          article.frontmatter.title.toLowerCase().includes(query) ||
          article.excerpt?.toLowerCase().includes(query) ||
          article.frontmatter.tags?.some((tag) =>
            tag.toLowerCase().includes(query)
          )
      );
    }

    return filtered;
  }, [articles, selectedTag, selectedCategory, searchQuery]);

  // Memoized displayed articles - only recalculates when filtered articles or current page changes
  const displayedArticles = useMemo(() => {
    return filteredArticles.slice(0, currentPage * ARTICLES_PER_PAGE);
  }, [filteredArticles, currentPage]);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTag, selectedCategory, searchQuery]);

  // Load more articles
  const loadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedTag("");
    setSelectedCategory("");
    setSearchQuery("");
  };

  // Check if there are more articles to load
  const hasMore = displayedArticles.length < filteredArticles.length;

  // Early return for empty articles
  if (!articles.length) {
    return (
      <div className="article-archive">
        <div className="loading-container">
          <h1>Articles</h1>
          <p>No articles found.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <StyledArticleArchive.OutlineWrapper>
        {displayedArticles.map((article) => (
          <MemoizedArticleCard
            key={article.id || article.slug}
            article={article}
          />
        ))}
      </StyledArticleArchive.OutlineWrapper>

      {/* Empty state */}
      {displayedArticles.length === 0 && (
        <div className="empty-state">
          <h2>No articles found</h2>
          <p>Try adjusting your search or filter criteria.</p>
          {(selectedTag || selectedCategory || searchQuery) && (
            <button onClick={clearFilters} className="clear-filters-button">
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && (
        <div className="load-more-container">
          <button onClick={loadMore} className="load-more-button">
            Load More Articles (
            {filteredArticles.length - displayedArticles.length} remaining)
          </button>
        </div>
      )}

      {/* End message */}
      {!hasMore && displayedArticles.length > 0 && (
        <div className="end-message">
          {/* <p>You've seen all articles! 🎉</p> */}
        </div>
      )}
    </>
  );
};
